import React, { useState, useEffect, useRef } from 'react';
import { Shield, Globe, Wifi, Users, ArrowRight, CheckCircle } from 'lucide-react';
import { mockData } from '../mock';

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const sectionRef = useRef(null);

  const iconMap = {
    Shield,
    Globe,
    Wifi,
    Users
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-24 bg-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="cyber-grid opacity-10" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-cyan-500/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-l from-blue-500/5 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 rounded-full glass-effect mb-6">
            <Shield className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-cyan-400">Nossos Serviços</span>
          </div>
          
          <h2 className="heading-1 mb-6">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Serviços de Pentest Especializados
            </span>
          </h2>
          
          <p className="body-large text-gray-300 max-w-3xl mx-auto">
            Oferecemos uma gama completa de serviços de segurança cibernética, 
            desde testes de intrusão até análise de vulnerabilidades avançadas.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8 mb-16">
          {mockData.services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const isActive = activeService === index;
            
            return (
              <div
                key={service.id}
                className={`group relative p-8 glass-effect border border-white/10 rounded-lg cursor-pointer transition-all duration-500 hover:border-cyan-400/50 hover:scale-105 ${
                  isVisible ? 'animate-fadeInUp' : 'opacity-0'
                } ${isActive ? 'border-cyan-400 bg-cyan-400/5' : ''}`}
                style={{ animationDelay: `${index * 0.2}s` }}
                onClick={() => setActiveService(index)}
              >
                {/* Icon */}
                <div className="mb-6">
                  <div className={`w-16 h-16 glass-effect border rounded-full flex items-center justify-center transition-all duration-500 ${
                    isActive ? 'border-cyan-400 bg-cyan-400/10' : 'border-cyan-400/30 group-hover:border-cyan-400'
                  }`}>
                    <IconComponent className={`w-8 h-8 transition-all duration-500 ${
                      isActive ? 'text-cyan-400 scale-110' : 'text-cyan-400 group-hover:scale-110'
                    }`} />
                  </div>
                </div>

                {/* Content */}
                <h3 className="heading-3 text-white mb-4 group-hover:text-cyan-400 transition-colors duration-300">
                  {service.title}
                </h3>
                
                <p className="body-small text-gray-300 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features List */}
                <ul className="space-y-2 mb-6">
                  {service.features.slice(0, 3).map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-2 text-sm text-gray-400">
                      <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className="flex items-center text-cyan-400 font-medium group-hover:text-white transition-colors duration-300">
                  <span className="text-sm">Saber mais</span>
                  <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" />
                </div>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10" />
                
                {/* Active Indicator */}
                {isActive && (
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-t-lg" />
                )}
              </div>
            );
          })}
        </div>

        {/* Active Service Details */}
        <div className={`glass-effect border border-cyan-400/30 rounded-lg p-8 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '1s' }}>
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left Side - Service Details */}
            <div>
              <h3 className="heading-2 text-white mb-4">
                {mockData.services[activeService].title}
              </h3>
              
              <p className="body-medium text-gray-300 mb-6 leading-relaxed">
                {mockData.services[activeService].description}
              </p>

              {/* Full Features List */}
              <div className="grid sm:grid-cols-2 gap-3">
                {mockData.services[activeService].features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    <span className="text-sm text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Service Image */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxuZXR3b3JrJTIwc2VjdXJpdHklMjBzZXJ2ZXJ8ZW58MHx8fHwxNzU3Mzc3NTg2fDA&ixlib=rb-4.1.0&q=85"
                alt="Serviços de Pentest"
                className="w-full h-64 object-cover rounded-lg"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent rounded-lg" />
              
              {/* Floating Icon */}
              <div className="absolute top-4 right-4 w-12 h-12 glass-effect border border-cyan-400/50 rounded-full flex items-center justify-center">
                {React.createElement(iconMap[mockData.services[activeService].icon], {
                  className: "w-6 h-6 text-cyan-400"
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;